// Ported from dev-scrips-pm-react src/billing/components/open-cashier-shift-dialog.tsx (PROD-3312 appointment Billing
// tab, step 5). Flutter `open_cashier_shift_popup.dart`: read-only Shift date + User, an Opening Balance that must be
// >= 0, then Open | Cancel. POST /api/CashierShift/Open with `{ openingBalance: Number(parsed.toFixed(2)),
// category: 'Onsite' }`. The validation text is verbatim, trailing space included.
// Differences from pm-react, none of them behavioural:
//   - the form is its own component, mounted only while the dialog is open, so every open starts at '0' with no
//     message and a fresh mutation. pm-react reset the same three things in its close handler.
//   - the shift time is pm-react's DEFAULT 12-hour single-letter form ("1:43a"), fixed here: the PA's 12h / 24h
//     preference (`@/shared/lib/time-format`, PROD-3313) is routed through the calendar and scheduling only so far.
//   - classes: the sheet / column widths pm-react takes from arbitrary `w-[var(--…)]` values (banned here) are the DS
//     Modal's `md` width and equal flex columns; the custom 48px header bar is the DS Modal's own title row; the
//     no-spinner `[&_input]` variants are the DS TextField's own; the Open button's disabled colours are the bridge
//     Button's (pm-react overrides the primary button's).
import { useState } from 'react';
import { TextField } from '@scripsteam/scrips-signal-ds';
import {
  BridgeButton as Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  PatientAvatar,
} from '@/billing/bridge';
import { useAuthStore } from '@/auth/auth-store';
import { useOpenCashierShift } from '@/billing/services/use-cashier-shifts';

interface OpenCashierShiftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional: defaults to now. */
  shiftDate?: Date;
}

const OPENING_BALANCE_VALIDATION = 'Opening balance must be greater than or equal 0 ';

/** Flutter `scDateTimeFormat(DateTime.now())`: "MMM d, yyyy h:mma" with single-letter am/pm ("May 13, 2026 1:43a"). */
function formatShiftDate(d: Date): string {
  const datePart = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const h = d.getHours();
  const time = `${h % 12 === 0 ? 12 : h % 12}:${String(d.getMinutes()).padStart(2, '0')}${h >= 12 ? 'p' : 'a'}`;
  return `${datePart} ${time}`;
}

export function OpenCashierShiftDialog({ open, ...rest }: OpenCashierShiftDialogProps) {
  return open ? <OpenCashierShiftForm {...rest} /> : null;
}

function OpenCashierShiftForm({ onOpenChange, shiftDate = new Date() }: Omit<OpenCashierShiftDialogProps, 'open'>) {
  const user = useAuthStore((s) => s.user);
  const userName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || '—';

  const [openingBalanceInput, setOpeningBalanceInput] = useState('0');
  const [validationMessage, setValidationMessage] = useState('');

  const openShift = useOpenCashierShift();
  const submitting = openShift.isPending;

  const parsed = parseFloat(openingBalanceInput);
  const isValid = openingBalanceInput.trim().length > 0 && !Number.isNaN(parsed) && parsed >= 0;

  const handleOpeningBalanceChange = (value: string) => {
    setOpeningBalanceInput(value);
    const v = parseFloat(value);
    setValidationMessage(Number.isNaN(v) || v < 0 ? OPENING_BALANCE_VALIDATION : '');
  };

  const onSubmit = async () => {
    if (!isValid) return;
    try {
      await openShift.mutateAsync({ openingBalance: Number(parsed.toFixed(2)), category: 'Onsite' });
      onOpenChange(false);
    } catch {
      // The global toast surfaces it; the dialog stays open so the user can correct and retry.
    }
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent size="md" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Open Shift</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6 py-6">
          {/* Read-only metadata row: shift date + user. */}
          <div className="flex items-start gap-10">
            <div className="flex flex-1 flex-col gap-1">
              <label className="text-xs font-medium text-text-muted">Shift date</label>
              <div className="flex h-10 items-center rounded-md bg-grey-100 px-3 text-sm text-text-primary">
                {formatShiftDate(shiftDate)}
              </div>
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <label className="text-xs font-medium text-text-muted">User</label>
              <div className="flex h-10 items-center gap-2 truncate rounded-md bg-grey-100 px-3 text-sm text-text-primary">
                <PatientAvatar name={userName} variant="provider" size="sm" />
                <span className="truncate">{userName}</span>
              </div>
            </div>
          </div>

          {/* Opening balance: required, numeric, >= 0. */}
          <TextField
            label="Opening Balance"
            type="number"
            size="compact"
            inputMode="decimal"
            min={0}
            step="0.01"
            placeholder="Enter amount"
            value={openingBalanceInput}
            disabled={submitting}
            onChange={(e) => handleOpeningBalanceChange(e.target.value)}
            error={validationMessage || undefined}
          />

          {/* Flutter has Open then Cancel; the order is kept. */}
          <div className="flex items-center gap-4 pt-2">
            <Button type="button" onClick={onSubmit} disabled={!isValid || submitting}>
              {submitting ? 'Opening…' : 'Open'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="border-brand-primary bg-transparent text-brand-primary hover:bg-blue-100"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
